/**
 * Game entry: load data → build hex match → wire 3D renderer + input + HUD →
 * run a fixed-timestep simulation loop. Humans and bots both issue Commands
 * through executeCommand (the command pattern that makes bots/MP trivial).
 *
 * Open: /            (?cols=&rows=&seed= override the hex map)
 *        /demo.html  (static aesthetic demo)
 */
import "./hud.css";
import { loadData } from "./data/loader";
import { Registry } from "./data/registry";
import { createMatch, step, executeCommand, spawnBuilding, spawnUnit, snapshot, restore, type World, type WorldSnapshot } from "./sim";
import { FIXED_DT, type Command, type Eid } from "./sim/types";
import { Bot, type BotState } from "./ai/bot";
import { Renderer3d } from "./render/renderer3d";
import { Fog } from "./render/fog";
import { Input3d } from "./input/input3d";
import { Hud } from "./ui/hud";
import { icon } from "./ui/icons";
import { showLoading, hideLoading, showHome, showNewGame, showPause, hidePause } from "./ui/screens";
import { gzip, bytesToB64, b64ToBytes, readSave } from "./save-codec";

// DEV hot-reload for the bot AI. The match constructs `new Bot(...)` ONCE at setup,
// so a Vite module swap leaves the running bots on their OLD logic. This rebuilds
// the live match's bots in place with the freshly-loaded code (bot.ts + the tactics
// it imports) so edits apply WITHOUT restarting — a full page refresh + Continue
// also reloads the code (setup runs `new Bot` again), this just saves the refresh.
let activeBots: { world: World; bots: Bot[] } | null = null;
const viteHot = (import.meta as ImportMeta & { hot?: { accept(dep: string, cb: (m: unknown) => void): void } }).hot;
if (viteHot) {
  viteHot.accept("./ai/bot", (mod) => {
    const NewBot = (mod as { Bot?: typeof Bot } | undefined)?.Bot;
    if (!activeBots || !NewBot) return;
    const fresh = [...activeBots.world.players.values()].filter((p) => p.kind === "bot").map((p) => new NewBot(p.id));
    activeBots.bots.length = 0;
    activeBots.bots.push(...fresh);
    console.info("[hmr] bot AI reloaded — %d bot(s) re-initialised", fresh.length);
  });
}

main().catch((err) => {
  console.error(err);
  const app = document.getElementById("app");
  if (app) app.textContent = "Game failed: " + err.message;
});

// localStorage / sessionStorage keys for the save & boot-intent shell.
const LOAD_KEY = "tbg:loadOnBoot";
const SAVE_KEY = "tbg:save";
const AUTOSAVE_KEY = "tbg:autosave";
const INTENT_KEY = "tbg:intent";

type SaveRuntime = {
  simTime: number;
  bots: Record<string, BotState>;
};
type SavedMatch = WorldSnapshot & { runtime?: SaveRuntime };

function savedRuntime(value: unknown): SaveRuntime | undefined {
  const runtime = (value as { runtime?: unknown } | null)?.runtime;
  if (!runtime || typeof runtime !== "object") return undefined;
  const candidate = runtime as Partial<SaveRuntime>;
  const simTime = candidate.simTime;
  if (typeof simTime !== "number" || !Number.isFinite(simTime) || !candidate.bots || typeof candidate.bots !== "object") return undefined;
  const bots: Record<string, BotState> = {};
  for (const [id, state] of Object.entries(candidate.bots)) {
    const b = state as Partial<BotState> | null;
    if (!b || !Number.isFinite(b.nextThink) || !Number.isFinite(b.nextExpand) ||
      !Number.isFinite(b.workerTarget) || !Number.isFinite(b.armyTarget) ||
      typeof b.attackWaveSent !== "boolean" ||
      !Array.isArray(b.assignedWorkers) || !Array.isArray(b.committed) ||
      !b.assignedWorkers.every(Number.isInteger) || !b.committed.every(Number.isInteger)) continue;
    bots[id] = b as BotState;
  }
  return { simTime, bots };
}

/** Wait two animation frames so a just-set loading message actually paints
 *  before the next blocking step (terrain gen / renderer build). */
const nextFrame = (): Promise<void> =>
  new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));

/** Open a file picker, normalize the chosen save to base64-gzip, stash it, and
 *  reload into the game (boot restores it). Shared by the home + pause menus. */
function loadGameFromFile(): void {
  const fileIn = document.createElement("input");
  fileIn.type = "file";
  fileIn.accept = "application/json,application/gzip,.gz,.json";
  fileIn.addEventListener("change", () => {
    const f = fileIn.files?.[0];
    if (!f) return;
    const fr = new FileReader();
    fr.onload = async () => {
      try {
        const json = await readSave(new Uint8Array(fr.result as ArrayBuffer));
        JSON.parse(json); // validate it parses before committing to a reload
        localStorage.setItem(LOAD_KEY, bytesToB64(await gzip(json)));
        sessionStorage.setItem(INTENT_KEY, JSON.stringify({ kind: "load" }));
        location.reload();
      } catch { alert("Not a valid save file."); }
    };
    fr.readAsArrayBuffer(f);
  });
  fileIn.click();
}

/** Title screen over a slowly auto-orbiting low-res (50×50) backdrop world. */
function showHomeScreen(reg: Registry): void {
  const seed = Math.floor(Math.random() * 1_000_000);
  const world = createMatch(reg, "duel", { cols: 50, rows: 50, seed });
  const renderer = new Renderer3d(world);
  renderer.setWorld(world);
  renderer.inputEnabled = false;       // no WASD/edge/Q-E on the title backdrop…
  renderer.controls.enabled = false;   // …and no mouse-wheel zoom / drag either
  renderer.controls.autoRotate = true; // (autoRotate still runs via controls.update)
  renderer.controls.autoRotateSpeed = 0.35;
  // gentle pull-back from the in-game start distance (not all the way to orbit)
  renderer.setDistance(renderer.camera.position.distanceTo(renderer.controls.target) * 1.5);
  document.getElementById("app")!.appendChild(renderer.renderer.domElement);
  const loop = () => { requestAnimationFrame(loop); renderer.draw(world); };
  requestAnimationFrame(loop);

  const start = (i: object) => { sessionStorage.setItem(INTENT_KEY, JSON.stringify(i)); location.reload(); };
  showHome({
    hasContinue: !!localStorage.getItem(AUTOSAVE_KEY),
    onContinue: () => start({ kind: "continue" }),
    onNew: () => showNewGame({
      defaults: { seed: Math.floor(Math.random() * 1_000_000), cols: 150, rows: 90 },
      civs: Object.values(reg.data.civs).map((c) => ({ id: c.id, name: c.name })),
      onStart: (cfg) => start({ kind: "new", ...cfg }),
      onCancel: () => { /* dismiss; stay on home */ },
    }),
    onLoad: loadGameFromFile,
  });
}

async function main() {
  const setLoad = showLoading("Loading assets…");
  const data = await loadData();
  const reg = new Registry(data);

  // boot intent (set by the home/pause menus before a reload), one-shot
  const intentRaw = sessionStorage.getItem(INTENT_KEY);
  sessionStorage.removeItem(INTENT_KEY);
  let intent: { kind: string; seed?: number; cols?: number; rows?: number; players?: { civ: string; kind: "human" | "bot"; team: number }[] } | null = null;
  try { intent = intentRaw ? JSON.parse(intentRaw) : null; } catch { intent = null; }

  // legacy URL overrides (/?seed=&cols=&rows=) still launch straight into a game
  const params = new URLSearchParams(location.search);
  const pCols = Math.abs(Number(params.get("cols")) | 0);
  const pRows = Math.abs(Number(params.get("rows")) | 0);
  const pSeed = params.get("seed") ? Number(params.get("seed")) || 1337 : 0;

  // Nothing requested → show the title screen over a live backdrop, and stop.
  if (!intent && !pSeed && !pCols) {
    hideLoading();
    return showHomeScreen(reg);
  }

  setLoad("Generating map…");
  await nextFrame();
  const readSaveB64 = async (b64: string): Promise<{ world: World; runtime?: SaveRuntime }> => {
    const saved = JSON.parse(await readSave(b64ToBytes(b64))) as SavedMatch;
    return { world: restore(saved, reg), runtime: savedRuntime(saved) };
  };
  const freshWorld = () => createMatch(reg, "duel", { cols: 150, rows: 90, seed: 1337 });
  const VIEW_KEY = "tbg:camView";
  // restore the saved camera view only when RESUMING the same match (load/continue);
  // a brand-new game centres on the town centre instead.
  let resumingView = false;
  let resumedRuntime: SaveRuntime | undefined;
  let world: World;
  try {
    if (intent?.kind === "load") {
      const b64 = localStorage.getItem(LOAD_KEY);
      localStorage.removeItem(LOAD_KEY);
      const loaded = b64 ? await readSaveB64(b64) : undefined;
      world = loaded?.world ?? freshWorld();
      resumedRuntime = loaded?.runtime;
      resumingView = !!b64;
    } else if (intent?.kind === "continue") {
      const b64 = localStorage.getItem(AUTOSAVE_KEY);
      const loaded = b64 ? await readSaveB64(b64) : undefined;
      world = loaded?.world ?? freshWorld();
      resumedRuntime = loaded?.runtime;
      resumingView = !!b64;
    } else {
      localStorage.removeItem(AUTOSAVE_KEY); // a brand-new map shouldn't resume the old autosave
      localStorage.removeItem(VIEW_KEY);     // …nor its camera view
      world = createMatch(reg, "duel", {
        cols: intent?.cols || pCols || 150,
        rows: intent?.rows || pRows || 90,
        seed: intent?.seed || pSeed || 1337,
        players: intent?.players, // New Game roster (undefined → map's default civs)
      });
    }
  } catch (e) {
    console.error("Failed to start game:", e);
    world = freshWorld();
  }
  setLoad("Building world…");
  await nextFrame();

  /** Snapshot → gzip → localStorage (quick-save) + a downloaded .json.gz file. */
  const saveGame = async () => {
    const snap = snapshot(world);
    const gz = await gzip(encodeSave(snap));
    try { localStorage.setItem(SAVE_KEY, bytesToB64(gz)); } catch (e) { console.warn("localStorage save failed", e); }
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([gz as BlobPart], { type: "application/gzip" }));
    a.download = `boring-game-tick${snap.tick}.json.gz`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // Rolling autosave: every AUTOSAVE_SECS of sim time, snapshot → gzip → store,
  // so a page refresh resumes the latest tick. Kept cheap: snapshotJSON does a
  // single stringify (no clone), gzip runs async off the render path, and the
  // work is scheduled in idle time. A guard skips a tick if one is still saving.
  const AUTOSAVE_SECS = 12;
  let autosaving = false;
  const runIdle = (fn: () => void) =>
    (window.requestIdleCallback ?? ((f: () => void) => setTimeout(f, 0)))(fn);
  const autosave = () => {
    if (autosaving) return;
    autosaving = true;
    // do the whole thing in idle time — the stringify and gzip both stay off the
    // render path. snapshotJSON reads the world synchronously between frames, so
    // it captures a consistent state (a few ticks later than the trigger is fine
    // for a save).
    runIdle(() => {
      gzip(encodeSave())
        .then((gz) => { try { localStorage.setItem(AUTOSAVE_KEY, bytesToB64(gz)); } catch (e) { console.warn("autosave failed", e); } })
        .catch((e) => console.warn("autosave gzip failed", e))
        .finally(() => { autosaving = false; });
    });
  };

  // ---- debug overlay (FPS + live stats), toggled by F3 or the menu ----
  const DEBUG_KEY = "tbg:debug";
  const dbg = document.createElement("pre");
  Object.assign(dbg.style, {
    position: "fixed", top: "54px", right: "10px", zIndex: "55", margin: "0",
    padding: "8px 10px", borderRadius: "8px", background: "rgba(10,14,10,0.7)",
    color: "#b6f08a", font: "11px/1.45 ui-monospace, SFMono-Regular, Menlo, monospace",
    whiteSpace: "pre", pointerEvents: "none", textShadow: "0 1px 2px rgba(0,0,0,0.6)",
    backdropFilter: "blur(3px)", minWidth: "150px",
  } as Partial<CSSStyleDeclaration>);
  let debugOn = localStorage.getItem(DEBUG_KEY) === "1";
  const setDebug = (v: boolean) => { debugOn = v; localStorage.setItem(DEBUG_KEY, v ? "1" : "0"); dbg.style.display = v ? "block" : "none"; };
  setDebug(debugOn);
  document.body.appendChild(dbg);
  window.addEventListener("keydown", (e) => { if (e.key === "F3") { e.preventDefault(); setDebug(!debugOn); } });

  const human = [...world.players.values()].find((p) => p.kind === "human") ?? world.players.get(0)!;
  const localPlayer = human.id;
  const bots = [...world.players.values()]
    .filter((p) => p.kind === "bot")
    .map((p) => new Bot(p.id, resumedRuntime?.bots[String(p.id)]));
  activeBots = { world, bots }; // expose the live match for the bot-AI hot-reload above

  // renderer
  const renderer = new Renderer3d(world);
  renderer.setWorld(world);
  // fog of war: vision around the local player's TEAM. Pass the registry so vision
  // reflects CURRENT data los (applies to loaded games too, not the baked spawn los).
  const fog = new Fog();
  fog.setLocal(human.team, reg);
  renderer.fog = fog;
  (window as unknown as Record<string, unknown>).__fog = fog;
  (window as unknown as Record<string, unknown>).__world = world;
  (window as unknown as Record<string, unknown>).__renderer = renderer;
  document.getElementById("app")!.appendChild(renderer.renderer.domElement);

  // camera tilt: restore the saved angle, adjust with PageUp/PageDown or the menu
  const CAM_KEY = "tbg:camAngle";
  const savedCam = Number(localStorage.getItem(CAM_KEY));
  if (savedCam) renderer.setCamElevDeg(savedCam);
  const setCamAngle = (deg: number) => { renderer.setCamElevDeg(deg); localStorage.setItem(CAM_KEY, String(renderer.getCamElevDeg())); };
  window.addEventListener("keydown", (e) => {
    if (e.key === "PageUp") { e.preventDefault(); setCamAngle(renderer.getCamElevDeg() + 4); }
    else if (e.key === "PageDown") { e.preventDefault(); setCamAngle(renderer.getCamElevDeg() - 4); }
  });

  // ---- pause menu + gear button (Esc or the gear toggle it; pausing freezes
  //      the sim while the world keeps rendering) ----
  let paused = false;
  const syncGear = () => {
    gear.style.background = paused ? "rgba(255,122,26,0.14)" : "rgba(10,10,11,0.82)";
    gear.style.color = paused ? "#ff7a1a" : "#a1a1aa";
    gear.style.borderColor = paused ? "rgba(255,122,26,0.4)" : "rgba(245,245,247,0.10)";
  };
  const closePause = () => { paused = false; hidePause(); syncGear(); };
  const openPause = () => {
    paused = true;
    syncGear();
    showPause({
      onResume: closePause,
      onSave: () => { saveGame().catch((e) => console.error("save failed", e)); },
      onLoad: loadGameFromFile,
      onQuit: async () => {
        // snapshot so "Continue" resumes exactly here, then reload to the title
        try { localStorage.setItem(AUTOSAVE_KEY, bytesToB64(await gzip(encodeSave()))); } catch (e) { console.warn(e); }
        location.reload();
      },
      debugOn: () => debugOn,
      onToggleDebug: () => setDebug(!debugOn),
      camAngle: () => renderer.getCamElevDeg(),
      setCamAngle,
    });
  };
  const togglePause = () => (paused ? closePause() : openPause());

  const gear = document.createElement("button");
  gear.innerHTML = icon("settings", 20);
  gear.title = "Menu (Esc)";
  gear.setAttribute("aria-label", "Game menu");
  Object.assign(gear.style, {
    position: "fixed", top: "20px", left: "24px", zIndex: "61",
    width: "44px", height: "44px", borderRadius: "10px", cursor: "pointer",
    display: "grid", placeItems: "center",
    border: "1px solid rgba(245,245,247,0.10)", background: "rgba(10,10,11,0.82)", color: "#a1a1aa",
    backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
    boxShadow: "0 4px 16px rgba(0,0,0,0.45)", pointerEvents: "auto",
  } as Partial<CSSStyleDeclaration>);
  gear.addEventListener("click", (e) => { e.stopPropagation(); togglePause(); });
  document.body.appendChild(gear);
  // Escape is context-sensitive: close the menu if open, else cancel an active
  // build placement / selection, and only open the menu when nothing's left to
  // cancel — so cancelling a build doesn't accidentally pause the game.
  window.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    e.preventDefault();
    if (resolved) return;
    if (paused) return closePause();
    if (input.cancelContext()) return;
    openPause();
  });

  // center on the human's town center
  let tcEid: Eid | null = null;
  for (const [eid, ident] of world.ident) {
    if (ident.defId === "town_center" && world.owner.get(eid)?.player === localPlayer) { tcEid = eid; break; }
  }
  if (tcEid) renderer.centerOn(world, tcEid);
  // resuming a match → restore the camera exactly where it was (position, zoom,
  // rotation), so a reload doesn't dump the player back at the town centre.
  if (resumingView) {
    try {
      const raw = localStorage.getItem(VIEW_KEY);
      if (raw) renderer.setView(JSON.parse(raw));
    } catch { /* corrupt view → keep the TC-centred default */ }
  }
  const saveView = () => {
    try { localStorage.setItem(VIEW_KEY, JSON.stringify(renderer.getView())); } catch { /* quota */ }
  };
  // persist on tab hide / unload so the latest view survives a refresh
  addEventListener("pagehide", saveView);
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") saveView(); });

  const cmdQueue: Command[] = [];
  (window as unknown as Record<string, unknown>).__cmd = (c: Command) => cmdQueue.push(c);

  let hud: Hud;
  const input = new Input3d({
    localPlayer: () => localPlayer,
    world: () => world,
    reg: () => reg,
    renderer: () => renderer,
    onCommand: (c) => cmdQueue.push(c),
    onSelectionChange: () => { renderer.selected = input.selection; hud?.update(world, [...input.selection]); },
    onBuildModeChange: () => hud?.update(world, [...input.selection]),
  });
  hud = new Hud(reg, localPlayer, {
    onCommand: (c) => cmdQueue.push(c),
    buildMode: () => input.buildMode,
    setBuildMode: (id) => input.setBuildMode(id),
    focusUnit: (eid) => {
      input.selection.clear();
      input.selection.add(eid);
      renderer.selected = input.selection;
      hud.update(world, [...input.selection]);
    },
  });
  renderer.selected = input.selection;
  (window as unknown as Record<string, unknown>).__input = input;
  // dev/test conveniences (used by E2E verification; harmless in prod build)
  (window as unknown as Record<string, unknown>).__reg = reg;
  (window as unknown as Record<string, unknown>).__localPlayer = localPlayer;
  (window as unknown as Record<string, unknown>).__dev = {
    spawnBuilding: (id: string, col: number, row: number, done = true) =>
      spawnBuilding(world, reg, human.civ, id, localPlayer, { x: col, y: row }, !done),
    spawnUnit: (id: string, col: number, row: number) =>
      spawnUnit(world, reg, human.civ, id, localPlayer, world.hexCenter(col, row)),
  };

  // ---- minimap ----
  const mm = hud.minimap;
  const mmCtx = mm.getContext("2d")!;
  const MMW = mm.width, MMH = mm.height;
  let mmMinX = Infinity, mmMaxX = -Infinity, mmMinZ = Infinity, mmMaxZ = -Infinity;
  for (const h of world.hexes) {
    if (h.x < mmMinX) mmMinX = h.x;
    if (h.x > mmMaxX) mmMaxX = h.x;
    if (h.z < mmMinZ) mmMinZ = h.z;
    if (h.z > mmMaxZ) mmMaxZ = h.z;
  }
  const mmRangeX = (mmMaxX - mmMinX) || 1;
  const mmRangeZ = (mmMaxZ - mmMinZ) || 1;
  // aspect-fit the (usually non-square) map into the rectangular minimap and
  // CENTRE it, so a tall map gets symmetric side margins instead of a clipped
  // square with a black bar on one edge.
  const mmScale = Math.min(MMW / mmRangeX, MMH / mmRangeZ);
  const mmOffX = (MMW - mmRangeX * mmScale) / 2;
  const mmOffY = (MMH - mmRangeZ * mmScale) / 2;
  // static terrain layer, cached to an offscreen canvas (drawn once)
  const mmTerrain = document.createElement("canvas");
  mmTerrain.width = MMW;
  mmTerrain.height = MMH;
  const mtCtx = mmTerrain.getContext("2d")!;
  // full biome palette (matches the 3D renderer's BIOME_COLORS, incl. the newer
  // desert/savanna/jungle/taiga/snow biomes that used to fall back to grey).
  const BIOME_HEX: Record<string, string> = {
    deepWater: "#1f5f7a", water: "#2f7fa3", sand: "#c2a866", desert: "#d8b878",
    savanna: "#9ca350", grass: "#6f9e3c", forest: "#427a32", jungle: "#2c6b27",
    taiga: "#4a7158", rock: "#8c8478", mountain: "#c9ccd0", snow: "#eaf1f6",
  };
  mtCtx.fillStyle = "#0a0d12";
  mtCtx.fillRect(0, 0, MMW, MMH);
  const mmDot = Math.max(2.2, mmScale * Math.sqrt(3) * (96 / (world.cols * 1.5)) * 1.3);
  for (const h of world.hexes) {
    mtCtx.fillStyle = BIOME_HEX[h.biome] ?? "#444";
    mtCtx.fillRect((h.x - mmMinX) * mmScale + mmOffX - mmDot / 2, (h.z - mmMinZ) * mmScale + mmOffY - mmDot / 2, mmDot, mmDot);
  }
  const ownerColorStr = (owner: number | undefined): string =>
    owner === undefined ? "#ddd" : world.players.get(owner)?.color ?? "#cccccc";

  // minimap: LEFT-click/drag pans the camera; RIGHT-click moves the selected
  // units to that world point (suppress the browser context menu first).
  let mmDragging = false;
  const mmToWorld = (clientX: number, clientY: number): { x: number; z: number } => {
    const r = mm.getBoundingClientRect();
    const px = ((clientX - r.left) / r.width) * MMW;
    const py = ((clientY - r.top) / r.height) * MMH;
    return { x: (px - mmOffX) / mmScale + mmMinX, z: (py - mmOffY) / mmScale + mmMinZ };
  };
  const mmJumpTo = (clientX: number, clientY: number) => {
    const g = mmToWorld(clientX, clientY);
    renderer.panTo(g.x, g.z);
  };
  const mmMoveCmd = (clientX: number, clientY: number, queued: boolean) => {
    const g = mmToWorld(clientX, clientY);
    const entities = [...input.selection].filter(
      (e) => world.owner.get(e)?.player === localPlayer && world.ident.get(e)?.kind === "unit");
    if (entities.length) cmdQueue.push({ type: "move", player: localPlayer, entities, x: g.x, y: g.z, queued });
  };
  mm.addEventListener("contextmenu", (e) => e.preventDefault());
  mm.addEventListener("mousedown", (e) => {
    e.preventDefault();
    if (e.button === 2) { mmMoveCmd(e.clientX, e.clientY, e.shiftKey); return; } // right → move order
    if (e.button === 0) { mmDragging = true; mmJumpTo(e.clientX, e.clientY); }   // left → pan
  });
  addEventListener("mousemove", (e) => { if (mmDragging) mmJumpTo(e.clientX, e.clientY); });
  addEventListener("mouseup", () => { mmDragging = false; });

  function drawMinimap() {
    mmCtx.clearRect(0, 0, MMW, MMH);
    mmCtx.drawImage(mmTerrain, 0, 0);
    // fog of war: veil minimap hexes outside the team's current vision
    if (fog.enabled) {
      mmCtx.fillStyle = "rgba(7,9,13,0.6)";
      for (const h of world.hexes) {
        if (fog.isHexVisible(h.col * world.rows + h.row)) continue;
        const mx = (h.x - mmMinX) * mmScale + mmOffX;
        const my = (h.z - mmMinZ) * mmScale + mmOffY;
        mmCtx.fillRect(mx - mmDot / 2, my - mmDot / 2, mmDot, mmDot);
      }
    }
    // entities
    for (const [eid, tr] of world.transform) {
      const ident = world.ident.get(eid);
      if (!ident) continue;
      // fog: never plot an enemy on the minimap while it's out of the team's vision.
      if (fog.isEnemyOwned(world, eid) && !fog.isPosVisible(world, tr.pos.x, tr.pos.y)) continue;
      const owner = world.owner.get(eid)?.player;
      const mx = (tr.pos.x - mmMinX) * mmScale + mmOffX;
      const my = (tr.pos.y - mmMinZ) * mmScale + mmOffY;
      if (ident.kind === "building") {
        mmCtx.fillStyle = ownerColorStr(owner);
        mmCtx.fillRect(mx - 2, my - 2, 4, 4);
      } else if (ident.kind === "unit") {
        mmCtx.fillStyle = ownerColorStr(owner);
        mmCtx.fillRect(mx - 1, my - 1, 2, 2);
      }
    }
    // cartography: enemy buildings we've discovered stay on the minimap (dim) while
    // their spot is fogged — including ones razed since we last looked (we don't
    // know yet). Visible ones were already drawn full-bright above.
    if (fog.enabled) {
      mmCtx.globalAlpha = 0.5;
      for (const g of fog.buildingMemory.values()) {
        if (fog.isPosVisible(world, g.x, g.z)) continue;
        const mx = (g.x - mmMinX) * mmScale + mmOffX;
        const my = (g.z - mmMinZ) * mmScale + mmOffY;
        mmCtx.fillStyle = ownerColorStr(g.owner);
        mmCtx.fillRect(mx - 2, my - 2, 4, 4);
      }
      mmCtx.globalAlpha = 1;
    }
    // camera viewport: 4 screen corners → ground → minimap
    const corners: [number, number][] = [
      [0, 0], [innerWidth, 0], [innerWidth, innerHeight], [0, innerHeight],
    ];
    mmCtx.strokeStyle = "rgba(255,255,255,0.85)";
    mmCtx.lineWidth = 1;
    mmCtx.beginPath();
    let first = true;
    for (const [cx, cy] of corners) {
      const g = renderer.screenToGround(cx, cy);
      if (!g) continue;
      const mx = (g.x - mmMinX) * mmScale + mmOffX;
      const my = (g.z - mmMinZ) * mmScale + mmOffY;
      if (first) { mmCtx.moveTo(mx, my); first = false; }
      else mmCtx.lineTo(mx, my);
    }
    mmCtx.closePath();
    mmCtx.stroke();
  }

  // ---- main loop: fixed-timestep sim + render every frame ----
  let acc = 0;
  let last = performance.now();
  let simTime = resumedRuntime?.simTime ?? 0;
  let lastAutosave = 0;
  let resolved = false;
  const encodeSave = (snap: WorldSnapshot = snapshot(world)): string => JSON.stringify({
    ...snap,
    runtime: {
      simTime,
      bots: Object.fromEntries(bots.map((bot) => [String(bot.player), bot.snapshotState()])),
    } satisfies SaveRuntime,
  });
  let fps = 60; // smoothed (render rate)
  let lastDbg = 0;
  // Cap the EXPENSIVE work (WebGL draw + HUD) to 60fps — there is nothing to see
  // faster than that in a slow hex RTS, so a 120/144Hz display was redrawing the
  // same frame 2x for nothing. The sim stays decoupled: it advances by real
  // elapsed time every animation frame and only steps at its fixed 20Hz.
  const RENDER_HZ = 60;
  const renderInterval = 1000 / RENDER_HZ;
  let lastRender = 0;
  let simStepsAccum = 0;

  function frame(now: number) {
    requestAnimationFrame(frame);
    let dt = (now - last) / 1000;
    last = now;
    dt = Math.min(dt, 0.25);
    acc += dt;
    let steps = 0;
    if (paused || resolved) {
      acc = 0; // freeze the sim while paused — don't pile up a catch-up backlog
    } else {
      while (acc >= FIXED_DT && steps < 6) {
        while (cmdQueue.length) executeCommand(world, reg, cmdQueue.shift()!);
        for (const bot of bots) bot.think(world, reg, simTime, (c) => executeCommand(world, reg, c));
        step(world, reg);
        simTime += FIXED_DT;
        acc -= FIXED_DT;
        steps++;
      }
    }
    simStepsAccum += steps;

    // frame-rate cap: skip the draw if we rendered too recently (the -1ms slack
    // absorbs rAF jitter so a 120Hz display lands cleanly on 60, not 40).
    if (now - lastRender < renderInterval - 1) return;
    const renderMs = now - lastRender;
    lastRender = now;
    fps += ((1000 / Math.max(renderMs, 0.5)) - fps) * 0.1; // EMA of real render cadence

    // drop dead/destroyed entities from the selection so the HUD + overlays
    // update when selected units die (they used to linger in the panel).
    for (const e of input.selection) if (!world.transform.has(e)) input.selection.delete(e);
    renderer.selected = input.selection;
    renderer.debugNav = debugOn; // F3 also shows the collision/footprint wireframe
    renderer.draw(world);
    hud.update(world, [...input.selection]);
    drawMinimap();
    if (!resolved) checkWin();
    // stop autosaving once the match is over — a finished game must NOT linger as a
    // resumable autosave (clicking Continue would just replay the victory screen).
    if (!resolved && simTime - lastAutosave >= AUTOSAVE_SECS) { lastAutosave = simTime; autosave(); saveView(); }
    // debug overlay (throttled ~6Hz; info.render is fresh right after draw)
    if (debugOn && now - lastDbg > 160) { lastDbg = now; updateDebug(now, renderMs, simStepsAccum); }
    simStepsAccum = 0;
    if (!booted) { booted = true; hideLoading(); } // first full frame drawn → drop the loading screen
  }
  let booted = false;
  requestAnimationFrame(frame);

  // The match is over → wipe the resumable autosave + saved camera so the title
  // screen stops offering Continue/Resume for a finished game.
  const endMatch = (status: string) => {
    resolved = true;
    localStorage.removeItem(AUTOSAVE_KEY);
    localStorage.removeItem(VIEW_KEY);
    gear.style.display = "none";
    hud.showStatus(status, [{ label: "Menu", onClick: () => location.reload() }]);
  };
  function checkWin() {
    const me = world.players.get(localPlayer);
    if (me?.defeated) { endMatch("DEFEATED"); return; }
    // victory when every surviving player is on the local player's team (allies win together)
    const alive = [...world.players.values()].filter((p) => !p.defeated);
    const hasOpponent = [...world.players.values()].some((p) => p.team !== me?.team);
    if (world.players.size > 1 && me && hasOpponent && alive.every((p) => p.team === me.team)) {
      endMatch("VICTORY");
    }
  }

  function updateDebug(_now: number, frameMs: number, steps: number) {
    let u = 0, b = 0, r = 0;
    for (const [, id] of world.ident) {
      if (id.kind === "unit") u++;
      else if (id.kind === "building") b++;
      else r++;
    }
    const info = renderer.renderer.info.render; // counts from the frame just drawn
    const tris = info.triangles;
    const trisStr = tris >= 1e6 ? (tris / 1e6).toFixed(2) + "M" : Math.round(tris / 1e3) + "k";
    const zoom = renderer.camera.position.distanceTo(renderer.controls.target);
    const ps = world.players.get(localPlayer);
    const heap = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
    const heapStr = heap ? `\nheap  ${Math.round(heap.usedJSHeapSize / 1048576)} MB` : "";
    dbg.textContent =
      `FPS   ${fps.toFixed(0).padStart(3)}   ${frameMs.toFixed(1)} ms\n` +
      `tick  ${world.tick}   sim ${steps}x\n` +
      `ents  ${world.transform.size}  (u${u} b${b} r${r})\n` +
      `draws ${info.calls}   tris ${trisStr}\n` +
      `zoom  ${zoom.toFixed(1)}   pop ${ps ? ps.population + "/" + ps.populationCap : "-"}` +
      heapStr;
  }

}
